import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaderResponse,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor, CapacitorHttp, type HttpResponse as CapacitorHttpResponse } from '@capacitor/core';
import { Observable } from 'rxjs';
import { environment } from 'projects/shared/src/environments/environment';

@Injectable()
export class NativeHttpInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!Capacitor.isNativePlatform() || !this.isApiRequest(req.url)) {
      return next.handle(req);
    }

    return new Observable<HttpEvent<unknown>>((observer) => {
      observer.next(new HttpHeaderResponse({ headers: req.headers, status: 0, url: req.urlWithParams }));
      const requestBody = this.requestBody(req);

      CapacitorHttp.request({
        data: requestBody,
        headers: this.headers(req.headers, requestBody),
        method: req.method,
        responseType: this.responseType(req),
        shouldEncodeUrlParams: false,
        url: req.urlWithParams,
      })
        .then((response) => {
          const headers = new HttpHeaders(response.headers || {});

          if (response.status >= 200 && response.status < 300) {
            observer.next(
              new HttpResponse({
                body: this.responseBody(response, req),
                headers,
                status: response.status,
                statusText: `${response.status}`,
                url: response.url || req.urlWithParams,
              }),
            );
            observer.complete();
            return;
          }

          observer.error(
            new HttpErrorResponse({
              error: this.responseBody(response, req),
              headers,
              status: response.status,
              statusText: `${response.status}`,
              url: response.url || req.urlWithParams,
            }),
          );
        })
        .catch((error) => {
          observer.error(
            new HttpErrorResponse({
              error,
              status: 0,
              statusText: 'Native HTTP request failed',
              url: req.urlWithParams,
            }),
          );
        });
    });
  }

  private isApiRequest(url: string) {
    return [
      environment.apiBaseUrl,
      environment.apiWorkflowBaseUrl,
      environment.apiGatewayBaseUrl,
      environment.apiMessagingBaseUrl,
      environment.apiPolicyBaseUrl,
      environment.apiUserServiceBaseUrl,
    ]
      .filter((baseUrl): baseUrl is string => !!baseUrl)
      .some((baseUrl) => url === this.withoutTrailingSlash(baseUrl) || url.startsWith(this.withTrailingSlash(baseUrl)));
  }

  private headers(headers: HttpHeaders, body: unknown) {
    const normalizedHeaders = headers.keys().reduce(
      (accumulator, key) => ({
        ...accumulator,
        [key]: headers.getAll(key)?.join(',') || '',
      }),
      {} as Record<string, string>,
    );

    if (this.isPlainObject(body)) {
      delete normalizedHeaders['content-type'];
      normalizedHeaders['Content-Type'] = 'application/json';
    }

    return normalizedHeaders;
  }

  private requestBody(req: HttpRequest<unknown>) {
    if (req.method === 'GET' || req.method === 'HEAD') return undefined;
    return req.body;
  }

  private responseType(req: HttpRequest<unknown>) {
    return req.responseType === 'arraybuffer' || req.responseType === 'blob' ? 'arraybuffer' : req.responseType;
  }

  private responseBody(response: CapacitorHttpResponse, req: HttpRequest<unknown>) {
    if (req.responseType === 'text' && typeof response.data !== 'string') {
      return JSON.stringify(response.data);
    }

    return response.data;
  }

  private withTrailingSlash(value: string) {
    return value.endsWith('/') ? value : `${value}/`;
  }

  private withoutTrailingSlash(value: string) {
    return value.replace(/\/+$/, '');
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype;
  }
}
